// frontend/src/pages/Contact.jsx
import CyberLunaWindow from '../components/base/CyberLunaWindow';
import ContactForm from '../components/form/ContactForm';
import Seo from '../components/base/Seo';
import styles from './Contact.module.css';
import content from '../data/content.json';

const { contact } = content;
const { address } = content.global;

/**
 * Contact Page — Windows XP CyberLuna style
 *
 * Two-panel layout: left info window + right form window.
 * Static CyberLunaWindow instances (non-draggable, in-flow).
 */
const Contact = () => {
    return (
        <div className={styles.contactPage} style={{ '--hero-image': `url(${process.env.PUBLIC_URL}/images/building.jpeg)` }}>
            <Seo pageKey="contact" />
            <h1 className="sr-only">Contact The Boombox Miami</h1>
            <div className={styles.desktop}>
                <div className={styles.container}>
                    <div className={styles.layout}>

                        {/* ─── Left: Contact Information Window ─── */}
                        <aside className={styles.infoPanel}>
                            <CyberLunaWindow isStatic title={contact.info_window.title}>
                                {/* The title bar already shows this, so it is
                                    kept for the document outline only. */}
                                <h2 className="sr-only">{contact.info_window.heading}</h2>

                                {/* Email */}
                                <div className={styles.infoItem}>
                                    <div className={styles.infoIconBox}>✉</div>
                                    <div>
                                        <div className={styles.infoLabel}>{contact.info_window.email.label}</div>
                                        <a href={`mailto:${contact.info_window.email.address}`} className={styles.infoLink}>
                                            {contact.info_window.email.address}
                                        </a>
                                    </div>
                                </div>

                                {/* Location */}
                                <div className={styles.infoItem}>
                                    <div className={styles.infoIconBox}>⊙</div>
                                    <div>
                                        <div className={styles.infoLabel}>{contact.info_window.location.label}</div>
                                        <a
                                            href={address.maps_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.infoLink}
                                        >
                                            {address.line1}<br />
                                            {address.line2}
                                        </a>
                                    </div>
                                </div>

                                {/* Events */}
                                <div className={styles.infoItem}>
                                    <div className={styles.infoIconBox}>♪</div>
                                    <div>
                                        <div className={styles.infoLabel}>{contact.info_window.events.label}</div>
                                        <a
                                            href="https://shotgun.live/en/venues/the-boombox-miami"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.infoLink}
                                        >
                                            {contact.info_window.events.link_text}
                                        </a>
                                    </div>
                                </div>

                                {/* Social */}
                                <div className={styles.socialSection}>
                                    <div className={styles.socialLabel}>{contact.info_window.social.label}</div>
                                    <div className={styles.socialIcons}>
                                        <a
                                            href="https://instagram.com/theboomboxmiami"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.socialIcon}
                                            aria-label="Follow us on Instagram"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                            </svg>
                                        </a>
                                        <a
                                            href="https://www.tiktok.com/@theboomboxmiami"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.socialIcon}
                                            aria-label="Follow us on TikTok"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.75a4.85 4.85 0 01-1.01-.06z" />
                                            </svg>
                                        </a>
                                        <a
                                            href="https://www.linkedin.com/company/the-boombox-miami/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.socialIcon}
                                            aria-label="Follow us on LinkedIn"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                            </svg>
                                        </a>
                                    </div>
                                </div>
                            </CyberLunaWindow>

                            {/* Map Window */}
                            <CyberLunaWindow isStatic title={contact.map_window.title}>
                                <iframe
                                    src={address.maps_embed_url}
                                    title={address.maps_iframe_title}
                                    className={styles.mapFrame}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    allowFullScreen
                                />
                            </CyberLunaWindow>

                            {/* Quick Response Alert */}
                            <div className={styles.responseBox}>
                                <div className={styles.responseTitle}>⚠ {contact.quick_response.title}</div>
                                <p className={styles.responseText}>
                                    {contact.quick_response.text}
                                </p>
                            </div>
                        </aside>

                        {/* ─── Right: Inquiry Form Window ─── */}
                        <div className={styles.formPanel}>
                            <CyberLunaWindow isStatic title={contact.form_window.title}>
                                <ContactForm />
                            </CyberLunaWindow>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
